import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Select,
  Row,
  Col,
  theme,
  Spin,
  Empty,
  message,
  Modal,
  Form,
  DatePicker,
  InputNumber,
  Tooltip,
  Statistic,
  Progress,
} from "antd";
import {
  ThunderboltOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  LinkOutlined,
  EyeOutlined,
  ControlOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";
import dayjs from "dayjs";

const { Title, Text } = Typography;

// FCAS Market types
const FCAS_MARKETS = [
  { value: "RAISE_1S", label: "Raise 1S", color: "green" },
  { value: "RAISE_6S", label: "Raise 6S", color: "green" },
  { value: "RAISE_60S", label: "Raise 60S", color: "green" },
  { value: "RAISE_5MIN", label: "Raise 5M", color: "green" },
  { value: "LOWER_1S", label: "Lower 1S", color: "orange" },
  { value: "LOWER_6S", label: "Lower 6S", color: "orange" },
  { value: "LOWER_60S", label: "Lower 60S", color: "orange" },
  { value: "LOWER_5MIN", label: "Lower 5M", color: "orange" },
];

// API helper
const apiCall = async (url, options = {}) => {
  const token = localStorage.getItem("token");
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  return response.json();
};

export default function BiddingSummary() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [mappings, setMappings] = useState([]);
  const [controlGroups, setControlGroups] = useState([]);
  const [biddingRules, setBiddingRules] = useState([]);
  const [stats, setStats] = useState({ totalGroups: 0, activeRules: 0, todayBids: 0 });
  const [pageSize, setPageSize] = useState(20);

  // Modal states
  const [mappingModalVisible, setMappingModalVisible] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [form] = Form.useForm();

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [mappingsRes, groupsRes, rulesRes] = await Promise.all([
        apiCall("/api/bidding/group-mappings"),
        apiCall("/api/control-groups?hasDuid=true"),
        apiCall("/api/bidding/rules"),
      ]);

      if (mappingsRes.success) setMappings(mappingsRes.data || []);
      if (groupsRes.success) setControlGroups(groupsRes.data || []);
      if (rulesRes.success) setBiddingRules(rulesRes.data || []);

      // Calculate stats
      setStats({
        totalGroups: groupsRes.data?.length || 0,
        activeRules: rulesRes.data?.filter(r => r.is_active).length || 0,
        todayBids: mappingsRes.data?.reduce((acc, m) => acc + (m.today_bid_count || 0), 0) || 0,
      });
    } catch (error) {
      console.error("Failed to fetch data:", error);
      message.error("Failed to load bidding data");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddMapping = () => {
    setEditingMapping(null);
    form.resetFields();
    form.setFieldsValue({
      effective_from: dayjs(),
      priority: 100,
    });
    setMappingModalVisible(true);
  };

  const handleEditMapping = (mapping) => {
    setEditingMapping(mapping);
    form.setFieldsValue({
      control_group_id: mapping.control_group_id,
      rule_id: mapping.rule_id,
      effective_from: mapping.effective_from ? dayjs(mapping.effective_from) : null,
      effective_to: mapping.effective_to ? dayjs(mapping.effective_to) : null,
      priority: mapping.priority,
    });
    setMappingModalVisible(true);
  };

  const handleDeleteMapping = async (mappingId) => {
    Modal.confirm({
      title: "Remove Rule Mapping",
      content: "Are you sure you want to remove this bidding rule from the control group?",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const data = await apiCall(`/api/bidding/group-mappings/${mappingId}`, { method: "DELETE" });
          if (data.success) {
            message.success("Mapping removed successfully");
            fetchData();
          } else {
            message.error(data.message || "Failed to remove mapping");
          }
        } catch (error) {
          message.error("Failed to remove mapping");
        }
      },
    });
  };

  const handleSaveMapping = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        control_group_id: values.control_group_id,
        rule_id: values.rule_id,
        effective_from: values.effective_from?.format("YYYY-MM-DD"),
        effective_to: values.effective_to?.format("YYYY-MM-DD") || null,
        priority: values.priority || 100,
      };

      let data;
      if (editingMapping) {
        data = await apiCall(`/api/bidding/group-mappings/${editingMapping.mapping_id}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        data = await apiCall("/api/bidding/group-mappings", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }

      if (data.success) {
        message.success(editingMapping ? "Mapping updated" : "Mapping created");
        setMappingModalVisible(false);
        fetchData();
      } else {
        message.error(data.message || "Failed to save mapping");
      }
    } catch (error) {
      if (!error.errorFields) {
        message.error("Failed to save mapping");
      }
    }
  };

  const columns = [
    {
      title: "Control Group",
      key: "group",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.group_name}</Text>
          <Tag color="blue">{record.duid}</Tag>
        </Space>
      ),
    },
    {
      title: "Bidding Rule",
      key: "rule",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.rule_name || record.rule_id}</Text>
          <Space size={4}>
            {record.fcas_markets?.map(market => {
              const m = FCAS_MARKETS.find(f => f.value === market);
              return <Tag key={market} color={m?.color || "default"} style={{ margin: 0 }}>{m?.label || market}</Tag>;
            })}
          </Space>
        </Space>
      ),
    },
    {
      title: "Strategy",
      dataIndex: "bid_strategy",
      key: "strategy",
      render: (strategy) => <Tag color="purple">{strategy}</Tag>,
    },
    {
      title: "Effective Period",
      key: "period",
      render: (_, record) => (
        <span>
          {dayjs(record.effective_from).format("YYYY-MM-DD")}
          {" → "}
          {record.effective_to ? dayjs(record.effective_to).format("YYYY-MM-DD") : "∞"}
        </span>
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      align: "center",
      width: 80,
    },
    {
      title: "Today's Bids",
      dataIndex: "today_bid_count",
      key: "bids",
      align: "center",
      render: (count) => <Tag color={count > 0 ? "green" : "default"}>{count || 0}</Tag>,
    },
    {
      title: "Status",
      key: "status",
      align: "center",
      render: (_, record) => {
        const isActive = record.is_active && (!record.effective_to || dayjs(record.effective_to).isAfter(dayjs()));
        return (
          <Tag color={isActive ? "success" : "default"} icon={isActive ? <CheckCircleOutlined /> : <ClockCircleOutlined />}>
            {isActive ? "Active" : "Inactive"}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button type="text" icon={<EyeOutlined />} size="small" onClick={() => navigate(`/control-group/${record.control_group_id}`)} />
          </Tooltip>
          <Button type="text" icon={<EditOutlined />} size="small" onClick={() => handleEditMapping(record)} />
          <Button type="text" icon={<DeleteOutlined />} size="small" danger onClick={() => handleDeleteMapping(record.mapping_id)} />
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <PageLayout active="biddingSummary">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout active="biddingSummary">
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 8 }}>
          <ThunderboltOutlined style={{ marginRight: 12, color: token.colorPrimary }} />
          Bidding Summary
        </Title>
        <Text type="secondary">
          Overview of FCAS bidding configurations. Shows which control groups are using which bidding rules.
        </Text>
      </div>

      {/* Stats Cards */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="DUID-Registered Groups"
              value={stats.totalGroups}
              prefix={<ControlOutlined style={{ color: token.colorPrimary }} />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Active Bidding Rules"
              value={stats.activeRules}
              prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Today's Bids Submitted"
              value={stats.todayBids}
              prefix={<SyncOutlined style={{ color: "#1890ff" }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* Mapping Table */}
      <Card
        bordered={false}
        style={{ borderRadius: 12 }}
        title="Group-Rule Mappings"
        extra={
          <Space>
            <Button icon={<LinkOutlined />} onClick={() => navigate("/bidding/rules")}>
              Manage Rules
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddMapping}>
              Add Mapping
            </Button>
          </Space>
        }
      >
        <Table
          dataSource={mappings}
          columns={columns}
          rowKey="mapping_id"
          pagination={makePagination(pageSize, setPageSize)}
          locale={{ emptyText: <Empty description="No bidding mappings configured" /> }}
        />
      </Card>

      {/* Add/Edit Mapping Modal */}
      <Modal
        title={editingMapping ? "Edit Rule Mapping" : "Add Rule Mapping"}
        open={mappingModalVisible}
        onCancel={() => setMappingModalVisible(false)}
        onOk={handleSaveMapping}
        okText={editingMapping ? "Update" : "Create"}
        width={500}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="control_group_id"
            label="Control Group"
            rules={[{ required: true, message: "Please select a control group" }]}
          >
            <Select
              placeholder="Select control group"
              disabled={!!editingMapping}
              showSearch
              optionFilterProp="label"
              options={controlGroups.map(g => ({
                value: g.id,
                label: g.name + (g.duid ? ` (${g.duid})` : ""),
              }))}
              optionRender={(option) => {
                const group = controlGroups.find(g => g.id === option.value);
                return (
                  <Space>
                    <span>{group?.name}</span>
                    {group?.duid && <Tag color="blue">{group.duid}</Tag>}
                  </Space>
                );
              }}
            />
          </Form.Item>

          <Form.Item
            name="rule_id"
            label="Bidding Rule"
            rules={[{ required: true, message: "Please select a bidding rule" }]}
          >
            <Select
              placeholder="Select bidding rule"
              options={biddingRules.map(r => {
                // fcas_markets is stored as JSON array in DB - parse if string
                let markets = r.fcas_markets;
                if (typeof markets === 'string') {
                  try { markets = JSON.parse(markets); } catch (e) { markets = []; }
                }
                const marketStr = Array.isArray(markets) ? markets.join(', ') : (markets || 'N/A');
                return {
                  value: r.rule_id,
                  label: `${r.rule_name || r.rule_id} - ${marketStr} (${r.bid_strategy})`,
                };
              })}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="effective_from"
                label="Effective From"
                rules={[{ required: true, message: "Required" }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="effective_to" label="Effective To">
                <DatePicker style={{ width: "100%" }} placeholder="No end date" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="priority"
            label={
              <Space>
                Priority
                <Tooltip title="Lower number = higher priority. When multiple rules apply, higher priority wins.">
                  <InfoCircleOutlined />
                </Tooltip>
              </Space>
            }
          >
            <InputNumber min={1} max={999} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </PageLayout>
  );
}
